import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { PatientsShell } from "@/features/patients";
import { RecordGateContext } from "@/features/patients/record-gate";
import { requirePerm } from "@/lib/auth/guards";
import { can } from "@/lib/auth/permissions";

/**
 * `/patients` — the shell the patient routes stand in.
 *
 * The rail, the search, the status control and the pager live here
 * (`features/patients/patient-index.tsx`), so opening a row does not drop
 * the list or send a new search. The open record is memory on that shell:
 * the URL carries no patient id (Rachel, 08-10-2026 — `DECISIONS.md`).
 * The index child (`patients.index.tsx`) is the nobody-chosen sentence.
 * `patients.$.tsx` sends any deeper path back here. `VIEW_PATIENT` gates
 * the surface.
 *
 * `GET /v1/patients/{patientCode}/audit-log` and
 * `GET /v1/patients/{patientCode}/activity-log` are drawn on the open record
 * (edit history, and Activity). This file already declares the patient
 * surface, so neither read adds a route. The 14-10-2026 10:51 UTC file
 * adds no path: it requires the audit `type`. The 04:37 UTC file adds
 * `GET /v1/patients/hashtags`. That catalogue is the same surface, so it
 * adds no route. The 06:59 UTC file renames `{patientId}` to
 * `{patientCode}` and adds no path, so it adds no route either.
 * The 20-10-2026 07:21 UTC file adds no path: `PatientAdrEditable` drops
 * `hiclSeqNo` from `required` and keeps `drug`. The same three files
 * declare the surface. Free text on that `drug` string is drawn in the
 * ADR dialogue already on this route (`DECISIONS.md` 20-10-2026).
 * The 20-10-2026 09:50 UTC file adds no path either. The clinical read
 * and write gain `heights`. This file, the index child and `patients.$`
 * still declare the surface. Clinical status draws the current height
 * and the BMI (`DECISIONS.md`).
 * The 21-10-2026 10:18 UTC file removes the patient access log and adds
 * the activity log. Activity draws that read (`DECISIONS.md` 22-10-2026).
 *
 * THE EDIT GATE is computed here, where the session read already stands in
 * the eager graph, and carried to the record through `RecordGateProvider` —
 * the lazy record chunk reads a boolean instead of importing the session
 * modules (`record-gate.tsx` says what that costs otherwise).
 */
const authenticated = getRouteApi("/_authenticated");

export const Route = createFileRoute("/_authenticated/patients")({
  beforeLoad: requirePerm("VIEW_PATIENT"),
  component: function PatientsRoute() {
    const { session } = authenticated.useRouteContext();
    return (
      <RecordGateContext.Provider value={can(session, "EDIT_PATIENT")}>
        <PatientsShell />
      </RecordGateContext.Provider>
    );
  },
});
